import React, { useMemo, useEffect } from 'react';
import {
  Typography,
  Grid,
  Button,
  TextField,
  Divider,
  MenuItem,
  FormLabel,
  Select,
  Stack,
} from '@mui/material';
import { useFormik } from 'formik';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import validator from 'validator';
import { z, ZodString, ZodArray, ZodOptional } from 'zod';
import { useMutation } from '@apollo/client';
import { CREATE_MARKETPLACE_EVENT_MUTATION } from '../graphql';
import { Event, CustomField } from '../types';
import { EventInfoFormContainer } from '../styledComponents';
import { Fields } from '@pv/common/components';
import { retrieveSavedUTMParams } from '../../../utils/attribution_tracking';
import { usePostHog } from 'posthog-js/react';
import { useAuth } from '../../../contexts/AuthContext';
import { useGuestEventPlans } from '../../../hooks/useGuestEventPlans';
import { GuestInfo } from './GuestInfo';
import { useBooking } from '../useBooking';

interface FormValues {
  message: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  eventName: string;
  groupSize: string;
  eventPlanId: string;
  [key: string]: string | string[]; // Allow additional dynamic keys
}

const NEW_EVENT_PLAN_ID = 'new';

const useEventFormSchema = (
  minGroupSize: number,
  maxGroupSize?: number,
  customFields?: CustomField[],
) =>
  useMemo(() => {
    const addCustomFieldsToSchema = () => {
      const customFieldsSchema: Record<
        string,
        | ZodString
        | ZodArray<ZodString>
        | ZodOptional<ZodString>
        | ZodOptional<ZodArray<ZodString>>
      > = {};
      customFields?.forEach((field) => {
        if (field.dataType === 'multi_enum') {
          if (field.required) {
            customFieldsSchema[field.userFieldKey] = z.array(
              z.string({
                required_error: `${field.name} is required`,
              }),
            );
          } else {
            customFieldsSchema[field.userFieldKey] = z
              .array(z.string())
              .optional();
          }
        } else {
          if (field.required) {
            customFieldsSchema[field.userFieldKey] = z.string({
              required_error: `${field.name} is required`,
            });
          } else {
            customFieldsSchema[field.userFieldKey] = z.string().optional();
          }
        }
      });
      return customFieldsSchema;
    };
    const schema = z.object({
      email: z
        .string({ required_error: 'Email is required' })
        .email({ message: 'Enter a valid email' }),
      firstName: z.string({ required_error: 'First Name is required' }),
      lastName: z.string({ required_error: 'Last Name is required' }),
      phone: z
        .string({ required_error: 'Phone is required' })
        .refine(validator.isMobilePhone, 'Enter a valid phone number'),
      eventName: z.string({ required_error: 'Event Name is required' }),
      message: z.string({ required_error: 'Message is required' }),
      groupSize: z
        .string({ required_error: 'Group Size is required' })
        .refine(validator.isNumeric, 'Group Size must be a number')
        .refine(
          (val) => parseInt(val) >= minGroupSize,
          `This venue has a minimum group size of ${minGroupSize}`,
        )
        .refine(
          (val) => (maxGroupSize ? parseInt(val) <= maxGroupSize : true),
          `This space has a maximum group size of ${maxGroupSize}`,
        ),
      eventPlanId: z.string().optional(),
      ...addCustomFieldsToSchema(),
    });

    return schema;
  }, [minGroupSize, maxGroupSize, customFields]);

interface Props {
  onCompleted: (event: Event) => void;
  buttonText: string;
  origin: 'express_book' | 'availability_check';
  customFields?: CustomField[];
  startDate: string;
  startOffset: string;
  endOffset: string;
}
export const EventInfoForm = ({
  onCompleted,
  origin,
  buttonText,
  customFields,
  startDate,
  startOffset,
  endOffset,
}: Props) => {
  const posthog = usePostHog();
  const { guest, setLoginDialogOpen, setError: setAuthError } = useAuth();
  const { venue, space, minGroupSize, maxGroupSize } = useBooking();
  let { utmSource, utmCampaign } = retrieveSavedUTMParams();

  const eventFormSchema = useEventFormSchema(
    minGroupSize,
    maxGroupSize,
    customFields,
  );
  const validationSchema = toFormikValidationSchema(eventFormSchema);
  const { eventPlans } = useGuestEventPlans();

  const [createMarketplaceEvent] = useMutation(
    CREATE_MARKETPLACE_EVENT_MUTATION,
    {
      onCompleted: ({ createMarketplaceEvent }) => {
        if (createMarketplaceEvent.errors.length > 0) {
          if (
            createMarketplaceEvent.errors.some((error: any) =>
              error.message.includes('login required'),
            )
          ) {
            setAuthError('Please log in to continue.');
            setLoginDialogOpen(true);
          }
          console.error('Error creating event:', createMarketplaceEvent.errors);
        } else {
          onCompleted(createMarketplaceEvent.event);
        }
      },
    },
  );

  const handleSubmit = (values: FormValues) => {
    const groupSize = parseInt(values.groupSize);

    const payload = {
      venueSlug: venue.slug,
      name: values.eventName,
      origin: origin,
      eventPlanId:
        values.eventPlanId === NEW_EVENT_PLAN_ID ? null : values.eventPlanId,
      planner: {
        firstName: values.firstName || guest?.firstName,
        lastName: values.lastName || guest?.lastName,
        email: values.email || guest?.email,
        phone: values.phone || guest?.phone,
        attemptMatchByEmail: true,
      },
      spaceSlug: space.slug,
      dateTimeRange: {
        startDate: startDate && new Date(startDate),
        startOffset: parseInt(startOffset),
        endOffset: parseInt(endOffset),
      },
      utmSource: utmSource,
      utmCampaign: utmCampaign,
      groupSize: groupSize,
      message: values.message,
      venueUserFields: customFields?.reduce(
        (acc, field) => ({
          ...acc,
          [field.userFieldKey]: values[field.userFieldKey] || '',
        }),
        {},
      ),
    };
    createMarketplaceEvent({
      variables: {
        input: payload,
      },
    });

    posthog.capture('guest_submitted_inquiry', {
      venue_slug: venue.slug,
      venue_name: venue.name,
      space_slug: space.slug,
      space_name: space.name,
      group_size: groupSize,
      type: origin,
    });
  };

  const initialValues: FormValues = {
    message: '',
    email: guest?.email || '',
    firstName: guest?.firstName || '',
    lastName: guest?.lastName || '',
    phone: guest?.phone || '',
    eventName: '',
    groupSize: '',
    eventPlanId: NEW_EVENT_PLAN_ID,
    ...customFields?.reduce(
      (acc, field) => ({ ...acc, [field.userFieldKey]: '' }),
      {},
    ),
  };

  const formik = useFormik<FormValues>({
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    formik.setValues({
      ...formik.values,
      firstName: guest?.firstName || '',
      lastName: guest?.lastName || '',
      email: guest?.email || '',
      phone: guest?.phone || '',
    });
  }, [guest]);

  return (
    <EventInfoFormContainer>
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                fontSize: '24px',
                lineHeight: '32px',
              }}
            >
              Enter your event details
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <GuestInfo />
          </Grid>

          {!guest?.firstName && (
            <Grid item xs={6}>
              <TextField
                label="First Name"
                inputProps={{ autoComplete: 'firstName' }}
                {...formik.getFieldProps('firstName')}
                error={
                  formik.touched.firstName && Boolean(formik.errors.firstName)
                }
                helperText={formik.touched.firstName && formik.errors.firstName}
                required
                fullWidth
                variant={'outlined'}
                data-cy="event-info-form-first-name"
              />
            </Grid>
          )}

          {!guest?.lastName && (
            <Grid item xs={6}>
              <TextField
                label="Last Name"
                inputProps={{ autoComplete: 'lastName' }}
                {...formik.getFieldProps('lastName')}
                error={
                  formik.touched.lastName && Boolean(formik.errors.lastName)
                }
                helperText={formik.touched.lastName && formik.errors.lastName}
                required
                fullWidth
                variant={'outlined'}
                data-cy="event-info-form-last-name"
              />
            </Grid>
          )}

          {!guest?.email && (
            <Grid item xs={6}>
              <TextField
                type="email"
                label="Email"
                inputProps={{ autoComplete: 'email' }}
                required
                {...formik.getFieldProps('email')}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                fullWidth
                variant={'outlined'}
                data-cy="event-info-form-email"
              />
            </Grid>
          )}

          {!guest?.phone && (
            <Grid item xs={6}>
              <Fields.PvPhoneField
                label="Phone"
                required
                inputProps={{
                  autoComplete: 'tel',
                }}
                {...formik.getFieldProps('phone')}
                error={formik.touched.phone && Boolean(formik.errors.phone)}
                helperText={formik.touched.phone && formik.errors.phone}
                fullWidth
                data-cy="event-info-form-phone"
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <Divider />
          </Grid>
          {guest && eventPlans && eventPlans.length > 0 && (
            <Grid item xs={12}>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                flexWrap="wrap"
                gap={2}
              >
                <FormLabel
                  htmlFor="event-info-form-event-plan"
                  sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                  }}
                >
                  <Typography variant="body2">
                    Add inquiry to an existing event
                  </Typography>
                </FormLabel>

                <Select
                  id="event-info-form-event-plan"
                  size="small"
                  {...formik.getFieldProps('eventPlanId')}
                  onChange={(e) => {
                    formik.setFieldValue('eventPlanId', e.target.value);

                    const selectedEventPlan = eventPlans?.find(
                      (eventPlan: { id: string; name: string }) =>
                        eventPlan.id === e.target.value,
                    );
                    formik.setFieldValue('eventName', selectedEventPlan?.name);
                    formik.setFieldValue(
                      'groupSize',
                      selectedEventPlan?.groupSize?.toString() || '',
                    );
                  }}
                  variant="outlined"
                  data-cy="event-info-form-event-plan"
                >
                  {eventPlans.map((eventPlan: { id: string; name: string }) => (
                    <MenuItem key={eventPlan.id} value={eventPlan.id}>
                      {eventPlan.name}
                    </MenuItem>
                  ))}
                  <MenuItem value={NEW_EVENT_PLAN_ID}>
                    <Typography color="text.secondary">
                      + Create new event
                    </Typography>
                  </MenuItem>
                </Select>
              </Stack>
            </Grid>
          )}
          {formik.values.eventPlanId === NEW_EVENT_PLAN_ID && (
            <Grid item xs={12}>
              <TextField
                label="Event Name"
                {...formik.getFieldProps('eventName')}
                error={
                  formik.touched.eventName && Boolean(formik.errors.eventName)
                }
                helperText={formik.touched.eventName && formik.errors.eventName}
                fullWidth
                required
                variant="outlined"
                type="text"
                data-cy="event-info-form-event-name"
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              label="Group Size"
              {...formik.getFieldProps('groupSize')}
              error={
                formik.touched.groupSize && Boolean(formik.errors.groupSize)
              }
              helperText={formik.touched.groupSize && formik.errors.groupSize}
              fullWidth
              required
              variant="outlined"
              type="text"
              data-cy="event-info-form-group-size"
            />
          </Grid>
          {customFields?.map((field) => (
            <Grid item xs={12} key={field.userFieldKey}>
              <Fields.PvCustomField
                label={field.name}
                variant="outlined"
                userFieldDefinition={field}
                value={formik.values[field.userFieldKey] || ''}
                onChange={(value: any) => {
                  formik.setFieldValue(field.userFieldKey, value);
                }}
                onBlur={formik.handleBlur}
                required={field.required}
                forceValidate={formik.errors[field.userFieldKey]}
                error={
                  formik.touched[field.userFieldKey] &&
                  Boolean(formik.errors[field.userFieldKey])
                }
                helperText={
                  formik.touched[field.userFieldKey] &&
                  formik.errors[field.userFieldKey]
                }
              />
            </Grid>
          ))}
          <Grid item xs={12}>
            <TextField
              label="Tell us about your event"
              {...formik.getFieldProps('message')}
              error={formik.touched.message && Boolean(formik.errors.message)}
              helperText={formik.touched.message && formik.errors.message}
              fullWidth
              variant="outlined"
              required
              multiline
              minRows={8}
              maxRows={30}
              data-cy="event-info-form-message"
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              color="secondary"
              sx={{
                width: '100%',
                textTransform: 'none',
                padding: '12px 28px',
                fontSize: '16px',
              }}
              data-cy="event-info-form-submit"
            >
              {buttonText}
            </Button>
          </Grid>
        </Grid>
      </form>
    </EventInfoFormContainer>
  );
};
